/**
 * /it/roadmap-di-prodotto — product roadmap concept page (Italian).
 * Links /it/product-discovery + /it/registro-delle-decisioni.
 */
import { MarketingShell } from './MarketingShell';

export function ItRoadmapProdotto() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/roadmap-di-prodotto"
      hreflangLocales={['it']}
      documentTitle="Roadmap di prodotto: cos'è, esempio e modello · Draftlize"
      documentDescription="Cos'è una roadmap di prodotto, i formati e un esempio, e come collegarla alle decisioni perché resti aggiornata quando cambia lo scope."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · Roadmap</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.4vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            La roadmap di prodotto<br />
            <em>che regge ai cambi di scope.</em>
          </h1>
          <p className="lede">
            Una roadmap di prodotto mostra <em>dove</em> va il prodotto e <em>perché</em>: non ogni dettaglio, ma direzione e ordine. Il difficile di solito non è crearla, ma che finisce come una «slide congelata»: tre settimane dopo cambia una decisione, la roadmap no, e nessuno le crede più. Qui i formati, un esempio, e come collegarla alle decisioni perché resti vera.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Formati di roadmap che funzionano.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong> — evita la falsa precisione delle date; ideale all'inizio.</li>
            <li><strong>Per temi / outcome</strong> — ordina per obiettivi, non per feature.</li>
            <li><strong>Temporale (trimestri)</strong> — quando le dipendenze impongono date rigide.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>Mini-esempio (Now/Next/Later):</strong> Now — ridurre l'attrito di onboarding · Next — fatturazione self-service · Later — spazi di team. Ogni voce porta un <em>perché</em>: la decisione che l'ha messa in roadmap. È proprio ciò che la versione a slide perde.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Congelata vs viva</span>
            <h2 className="serif">Roadmap-slide <em>vs roadmap che vive con le decisioni.</em></h2>
          </div>
          <p>Una roadmap è solo la punta visibile di una catena di decisioni. Se le separi, invecchia in silenzio.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Roadmap collegata (Draftlize)</th>
              <th>Roadmap-slide</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Il «perché» di ogni voce</td><td className="ours yes">collegato a una scheda di decisione</td><td className="no">in testa / sparso</td></tr>
            <tr><td>Quando una decisione cambia</td><td className="ours yes">la voce diventa «stale»</td><td className="no">la roadmap resta sbagliata</td></tr>
            <tr><td>Tre settimane dopo</td><td className="ours yes">attuale o segnalata</td><td className="no">nessuno le crede</td></tr>
            <tr><td>Un agente IA la usa</td><td className="ours yes">legge il contesto via MCP</td><td className="partial">slide piatta</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La maggior parte delle roadmap non erano pianificate male: solo che nessuno le ha aggiornate nelle tre settimane dall'ultima decisione.
        </blockquote>
        <cite>Collega la roadmap alle decisioni e si aggiorna da sola quando la decisione cambia. Questo è Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Roadmap o backlog?', a: 'La roadmap mostra direzione e ordine a livello di outcome; il backlog è la lista prioritizzata del lavoro concreto. La roadmap risponde «perché / dove», il backlog «cosa dopo».' },
            { q: 'Quale orizzonte temporale?', a: 'Fin dove puoi dare direzione onestamente; spesso Now/Next/Later invece di trimestri fissi. La falsa precisione delle date è il motivo principale per cui una roadmap perde credibilità.' },
            { q: 'Come gestisco i cambi di scope?', a: 'Se cambia la decisione dietro una voce, la voce deve cambiare. In Draftlize sono collegate: la decisione modificata segna «stale» la parte di roadmap interessata.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          Correlati: <a href="/it/product-discovery" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>product discovery</a> · <a href="/it/registro-delle-decisioni" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro delle decisioni</a>
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Una roadmap che si aggiorna da sola <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Costruisci la roadmap come schede collegate, non come slide. Se una decisione cambia, la roadmap si aggiorna da sola — non tre settimane dopo.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
